import type { HsFrustumObjectInfo, HsFrustumRequestInfo, HsVector3View } from '@sibe/hyperscene';

/** The camera state a Frustum request describes. Matrices are column-major 4x4 arrays. */
export interface HsFrustumCameraInfo {
  worldMatrix: ArrayLike<number>;
  projectionMatrix: ArrayLike<number>;
  projectionMatrixInverse: ArrayLike<number>;
  viewportHeightPixels: number;
}

/** Applies a 4x4 matrix to a point with perspective division. */
function applyMatrix4(x: number, y: number, z: number, m: ArrayLike<number>): [number, number, number] {
  const w: number = 1 / (m[3] * x + m[7] * y + m[11] * z + m[15]);
  return [
    (m[0] * x + m[4] * y + m[8] * z + m[12]) * w,
    (m[1] * x + m[5] * y + m[9] * z + m[13]) * w,
    (m[2] * x + m[6] * y + m[10] * z + m[14]) * w,
  ];
}

/** Corners of the near (`depthNdc` 0) or far (1) plane in world space, in the order the API expects. */
export function hsCameraPlaneWorldPoints(camera: HsFrustumCameraInfo, depthNdc: number): HsVector3View[] {
  const corners: [number, number][] = [
    [-1, -1],
    [-1, 1],
    [1, 1],
    [1, -1],
  ];
  return corners.map(([cornerX, cornerY]: [number, number]) => {
    const [viewX, viewY, viewZ]: [number, number, number] = applyMatrix4(
      cornerX,
      cornerY,
      depthNdc,
      camera.projectionMatrixInverse
    );
    const [x, y, z]: [number, number, number] = applyMatrix4(viewX, viewY, viewZ, camera.worldMatrix);
    return { x, y, z };
  });
}

/** A world box that limits a Frustum request to the nodes whose bounds meet it. */
export interface HsFrustumCutBox {
  min: readonly [number, number, number];
  max: readonly [number, number, number];
}

/**
 * The body of a Frustum request for the camera. `nodeTags`, when given, limits every object kind to
 * nodes with one of the tags; the objects are copied, so the caller's list is not changed. `cutBox` becomes the
 * request's `cutArea`: the API returns only nodes whose bounds meet it.
 */
export function hsMakeFrustumRequest(
  camera: HsFrustumCameraInfo,
  frustumObjects: readonly HsFrustumObjectInfo[],
  sceneSessionId: string,
  nodeTags: readonly string[] = [],
  cutBox: HsFrustumCutBox | null = null,
  viewSessionId?: string
): HsFrustumRequestInfo {
  const world: ArrayLike<number> = camera.worldMatrix;
  return {
    cameraPosition: { x: world[12], y: world[13], z: world[14] },
    viewportHeightPixels: camera.viewportHeightPixels,
    projectionScale: camera.projectionMatrix[5],
    nearPlane: hsCameraPlaneWorldPoints(camera, 0),
    farPlane: hsCameraPlaneWorldPoints(camera, 1),
    // The API takes the box around these points.
    cutArea:
      cutBox === null
        ? []
        : [
            { x: cutBox.min[0], y: cutBox.min[1], z: cutBox.min[2] },
            { x: cutBox.max[0], y: cutBox.max[1], z: cutBox.max[2] },
          ],
    frustumObjects: frustumObjects.map((entry: HsFrustumObjectInfo) =>
      nodeTags.length > 0 ? { ...entry, nodeTags: [...nodeTags] } : { ...entry }
    ),
    sceneSessionId,
    ...(viewSessionId === undefined ? {} : { viewSessionId }),
  };
}

/** Comma-separated node tags from a settings field, trimmed and without empty entries. */
export function hsParseNodeTags(value: string): string[] {
  return value
    .split(',')
    .map((tag: string) => tag.trim())
    .filter((tag: string) => tag.length > 0);
}
